<script setup lang="ts">
import { computed } from "vue";
import {
  getSeverityLevel,
  type SeverityLevel,
} from "@/constants/notationSystems";

const props = withDefaults(
  defineProps<{
    value: string | null | undefined;
    size?: "sm" | "md" | "lg";
  }>(),
  {
    size: "md",
  }
);

const isNoGrade = computed(
  () => props.value === null || props.value === undefined || props.value === "0"
);

const displayValue = computed(() => {
  if (isNoGrade.value) return "";
  return props.value;
});

// La couleur suit la gravité, pas le libellé : "3U" (IQOA) et "D3P" (PM2I)
// désignent le même niveau et doivent se ressembler d'un catalogue à l'autre.
const LEVEL_CLASSES: Record<SeverityLevel, string> = {
  none: "grade-none",
  minor: "grade-success",
  moderate: "grade-warning",
  moderateEvolutive: "grade-warning",
  severe: "grade-danger",
  severeUrgent: "grade-danger",
};

const colorClass = computed(() => {
  if (isNoGrade.value) return "grade-none";
  const level = getSeverityLevel(props.value);
  return level ? LEVEL_CLASSES[level] : "grade-none";
});

const sizeClass = computed(() => `grade-${props.size}`);
</script>

<template>
  <div class="disorder-grade-badge" :class="[colorClass, sizeClass]">
    <span class="grade-text">{{ displayValue }}</span>
  </div>
</template>

<style lang="scss" scoped>
.disorder-grade-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  border-radius: 4px;
  position: relative;
  overflow: hidden;
  flex-shrink: 0;

  // Size variants
  &.grade-sm {
    min-width: 24px;
    height: 20px;
    font-size: 11px;
    padding: 0 4px;
  }

  &.grade-md {
    min-width: 32px;
    height: 26px;
    font-size: 13px;
    padding: 0 6px;
  }

  &.grade-lg {
    min-width: 40px;
    height: 32px;
    font-size: 15px;
    padding: 0 8px;
  }

  // Color variants
  &.grade-success {
    background-color: rgba(25, 135, 84, 0.15);
    color: #198754;
  }

  &.grade-warning {
    background-color: rgba(253, 126, 20, 0.15);
    color: #fd7e14;
  }

  &.grade-danger {
    background-color: rgba(220, 53, 69, 0.15);
    color: #dc3545;
  }

  &.grade-none {
    background-color: rgba(108, 117, 125, 0.15);
    color: #6c757d;
  }
}

.grade-text {
  position: relative;
  z-index: 1;
}
</style>
